import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  AlertCircle, GitBranch, Pencil, Plus, RefreshCw, Save, X,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import {
  inputCls, selectCls, labelCls, btnPrimary, btnSecondary,
  pageTitleCls, pageSubtitleCls,
} from '../../components/ui';

// ─── Tipos ─────────────────────────────────────────────────────────────────────

interface AppEstadio {
  id: number;
  empresa_id: number;
  codigo: string;
  descripcion: string;
  varfecha: string;
  activo: boolean;
}

// Columnas disponibles en emp_bloques que se usan como fecha ancla
const VARFECHA_OPCIONES: { value: string; label: string }[] = [
  { value: 'fecha_siembra',    label: 'Siembra (fecha_siembra)' },
  { value: 'fecha_induccion',  label: 'Inducción (fecha_induccion)' },
  { value: 'fecha_segunda',    label: 'Segunda cosecha (fecha_segunda)' },
  { value: 'fecha_semillero',  label: 'Semillero (fecha_semillero)' },
  { value: 'fecha_forza',      label: 'Forza (fecha_forza)' },
  { value: 'fecha_maduracion', label: 'Maduración (fecha_maduracion)' },
  { value: 'fecha_cosecha',    label: 'Cosecha (fecha_cosecha)' },
  { value: 'fecha_barbecho',   label: 'Barbecho (fecha_barbecho)' },
];

const VARFECHA_LABEL: Record<string, string> = Object.fromEntries(
  VARFECHA_OPCIONES.map(o => [o.value, o.label])
);

// ─── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ msg, ok, onClose }: { msg: string; ok: boolean; onClose: () => void }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return ReactDOM.createPortal(
    <div className="fixed bottom-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium"
      style={{
        background: ok ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)',
        border: `1px solid ${ok ? 'var(--tone-success-border)' : 'var(--tone-warn-border)'}`,
        color: ok ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
      }}>
      {msg}
      <button onClick={onClose}><X size={14} /></button>
    </div>,
    document.body
  );
}

// ─── Modal Editar / Crear ──────────────────────────────────────────────────────

interface FormEstadio {
  codigo: string;
  descripcion: string;
  varfecha: string;
  activo: boolean;
}

const EMPTY_FORM: FormEstadio = {
  codigo: '',
  descripcion: '',
  varfecha: 'fecha_siembra',
  activo: true,
};

function ModalEstadio({
  estadio,
  empresaId,
  onSaved,
  onClose,
}: {
  estadio: AppEstadio | null;
  empresaId: number;
  onSaved: () => void;
  onClose: () => void;
}) {
  const isNew = estadio === null;
  const [form, setForm] = useState<FormEstadio>(
    isNew ? EMPTY_FORM : {
      codigo:      estadio.codigo,
      descripcion: estadio.descripcion,
      varfecha:    estadio.varfecha,
      activo:      estadio.activo,
    }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  function set<K extends keyof FormEstadio>(k: K, v: FormEstadio[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  async function guardar() {
    if (!form.codigo.trim()) { setError('El código es obligatorio'); return; }
    if (!form.varfecha)      { setError('Seleccioná el campo de fecha'); return; }
    setSaving(true);
    setError('');

    const payload = {
      empresa_id:  empresaId,
      codigo:      form.codigo.trim().toUpperCase(),
      descripcion: form.descripcion.trim(),
      varfecha:    form.varfecha,
      activo:      true,
    };

    const { error: dbErr } = isNew
      ? await supabase.from('app_estadios').insert(payload)
      : await supabase.from('app_estadios').update(payload).eq('id', estadio!.id);

    setSaving(false);
    if (dbErr) { setError(dbErr.message); return; }
    onSaved();
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={onClose}>
      <div className="rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
        style={{ background: 'var(--surface-overlay)', border: '1px solid var(--line)' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 shrink-0"
          style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <div className="flex items-center gap-2">
            <GitBranch size={15} style={{ color: 'var(--emp-accent-txt)' }} />
            <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>
              {isNew ? 'Nuevo Estadio VFP' : `Editar — ${estadio!.codigo}`}
            </span>
          </div>
          <button onClick={onClose} className="p-1 rounded" style={{ color: 'var(--ink-muted)' }}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">

          {/* Código */}
          <div>
            <label className={labelCls}>Código VFP</label>
            <input
              className={inputCls}
              value={form.codigo}
              onChange={e => set('codigo', e.target.value)}
              placeholder="Ej. DPC, FPC, BAR…"
              disabled={!isNew}
              style={!isNew ? { opacity: 0.6 } : {}}
            />
            <p className="text-[11px] mt-1" style={{ color: 'var(--ink-faint)' }}>
              Código tal como aparece en el campo <code>estadio_vfp</code> del bloque.
            </p>
          </div>

          {/* Descripción */}
          <div>
            <label className={labelCls}>Descripción</label>
            <input
              className={inputCls}
              value={form.descripcion}
              onChange={e => set('descripcion', e.target.value)}
              placeholder="Ej. Desarrollo Primera Cosecha"
            />
          </div>

          {/* Varfecha */}
          <div>
            <label className={labelCls}>Campo de fecha ancla (varfecha)</label>
            <select className={selectCls} value={form.varfecha}
              onChange={e => set('varfecha', e.target.value)}>
              {VARFECHA_OPCIONES.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className="text-[11px] mt-1" style={{ color: 'var(--ink-faint)' }}>
              Columna de <code>emp_bloques</code> que se usa como día 0 para calcular la fecha de aplicación.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
              style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)', color: 'var(--tone-warn-text)' }}>
              <AlertCircle size={13} />
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 flex justify-end gap-2 shrink-0"
          style={{ borderTop: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
          <button className={btnSecondary} onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button className={btnPrimary} onClick={guardar} disabled={saving}>
            <Save size={14} />
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Página Principal ──────────────────────────────────────────────────────────

export function EstadiosVfpPage() {
  const empresaId = useEmpresaId();

  const [estadios, setEstadios] = useState<AppEstadio[]>([]);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState<AppEstadio | 'new' | null>(null);
  const [toast, setToast]       = useState<{ msg: string; ok: boolean } | null>(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('app_estadios')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('codigo');
    setLoading(false);
    if (error) {
      setToast({ msg: `Error al cargar: ${error.message}`, ok: false });
      return;
    }
    setEstadios(data ?? []);
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  function onSaved() {
    setModal(null);
    setToast({ msg: 'Estadio guardado', ok: true });
    cargar();
  }

  const estadioEditar = modal === 'new' ? null : (modal as AppEstadio | null);

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* Header */}
      <div className="px-4 py-3 shrink-0 flex items-center gap-4"
        style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
        <div className="flex-1">
          <h1 className={pageTitleCls}>Estadios</h1>
          <p className={pageSubtitleCls}>
            Mapeo código de estadio → campo de fecha ancla para generación de boletas
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={cargar}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
          <button
            onClick={() => setModal('new')}
            className={`flex items-center gap-1.5 ${btnPrimary}`}>
            <Plus size={13} />
            Nuevo estadio
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: 'var(--emp-accent)' }} />
          </div>
        ) : estadios.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 opacity-40">
            <GitBranch size={36} style={{ color: 'var(--ink-faint)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>
              No hay estadios configurados. Creá uno con el botón de arriba.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, background: 'var(--surface-raised)', zIndex: 1 }}>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                {['Código', 'Descripción', 'Campo de fecha ancla (varfecha)', 'Estado', ''].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: 'var(--ink-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {estadios.map((e, i) => (
                <tr key={e.id}
                  style={{
                    borderBottom: '1px solid var(--line-dim)',
                    background: i % 2 === 0 ? 'transparent' : 'var(--surface-deep)',
                  }}>
                  {/* Código */}
                  <td className="px-4 py-3">
                    <span className="font-mono font-bold text-[13px] px-2 py-0.5 rounded"
                      style={{ background: 'var(--emp-accent-bg)', color: 'var(--emp-accent-txt)' }}>
                      {e.codigo}
                    </span>
                  </td>
                  {/* Descripción */}
                  <td className="px-4 py-3" style={{ color: 'var(--ink)' }}>
                    {e.descripcion || <span style={{ color: 'var(--ink-faint)' }}>—</span>}
                  </td>
                  {/* Varfecha */}
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs px-2 py-0.5 rounded"
                      style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                      {e.varfecha}
                    </span>
                    <span className="ml-2 text-xs" style={{ color: 'var(--ink-faint)' }}>
                      {VARFECHA_LABEL[e.varfecha] ?? ''}
                    </span>
                  </td>
                  {/* Activo */}
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded text-[11px] font-semibold"
                      style={e.activo
                        ? { background: 'var(--tone-success-bg)', border: '1px solid var(--tone-success-border)', color: 'var(--tone-success-text)' }
                        : { background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-faint)' }}>
                      {e.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  {/* Acciones */}
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setModal(e)}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors ml-auto"
                      style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                      <Pencil size={12} />
                      Editar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Info box */}
      <div className="px-4 py-3 shrink-0 text-xs"
        style={{ borderTop: '1px solid var(--line)', background: 'var(--surface-deep)', color: 'var(--ink-faint)' }}>
        <strong style={{ color: 'var(--ink-muted)' }}>Cómo funciona:</strong>{' '}
        Cada bloque tiene un <code>estadio_vfp</code> (p.ej. <code>DPC</code>). Al generar boletas, se busca aquí
        el <em>campo de fecha ancla</em> correspondiente y se suma los días del ciclo del plan de manejo para
        calcular la fecha de aplicación. Cambiá el campo de fecha sin tocar el código.
      </div>

      {/* Modal editar/crear */}
      {modal !== null && (
        <ModalEstadio
          estadio={estadioEditar}
          empresaId={empresaId}
          onSaved={onSaved}
          onClose={() => setModal(null)}
        />
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />}
    </div>
  );
}
